import { AlertCircle, PackageSearch } from 'lucide-react';

/** Shared skeleton/empty/error primitives for the storefront -- deliberately separate from `features/admin/components/primitives/` (styled for dense dashboard UX, not consumer-facing browsing/cart/checkout), same relationship as the storefront's own `lib/format.ts` mirroring `features/admin/lib/format.ts`. */

export function StorefrontSkeleton({ className = 'h-40 w-full' }: { className?: string }) {
  return <div className={`animate-pulse rounded-2xl bg-gray-100 ${className}`} aria-hidden="true" />;
}

export function StorefrontSkeletonGrid({ count = 8, className = 'h-64' }: { count?: number; className?: string }) {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" role="status" aria-label="Loading products">
      {Array.from({ length: count }).map((_, index) => (
        <StorefrontSkeleton key={index} className={className} />
      ))}
    </div>
  );
}

export interface StorefrontEmptyStateProps {
  title: string;
  description?: string;
  action?: React.ReactNode;
}

export function StorefrontEmptyState({ title, description, action }: StorefrontEmptyStateProps) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-16 text-center">
      <PackageSearch className="h-10 w-10 text-gray-300" aria-hidden="true" />
      <p className="text-base font-semibold text-gray-900">{title}</p>
      {description && <p className="max-w-sm text-sm text-gray-500">{description}</p>}
      {action}
    </div>
  );
}

export interface StorefrontErrorStateProps {
  message?: string;
  onRetry?: () => void;
}

export function StorefrontErrorState({ message = 'Something went wrong. Please try again.', onRetry }: StorefrontErrorStateProps) {
  return (
    <div role="alert" className="flex flex-col items-center gap-3 rounded-2xl border border-red-100 bg-red-50 px-6 py-12 text-center">
      <AlertCircle className="h-8 w-8 text-red-500" aria-hidden="true" />
      <p className="text-sm font-medium text-red-700">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="focus-visible:ring-brand rounded-full border border-red-200 bg-white px-4 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-100 focus-visible:outline-none focus-visible:ring-2"
        >
          Try again
        </button>
      )}
    </div>
  );
}

export function StorefrontLoadingState({ label = 'Loading…' }: { label?: string }) {
  return (
    <div role="status" className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500">
      <span className="border-brand h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" aria-hidden="true" />
      {label}
    </div>
  );
}
